import Phaser from 'phaser';
import type { Variant } from '@/domain/types/common';
import { VARIANT_VISUALS } from '@/domain/types/pet';

/**
 * variantFx — shared helpery dla wizualnych efektów wariantu peta (sesja 29B).
 *
 * **Powstał z extraction z `Pet.ts`** — gdy hatch cinematic w `HatchDirector`
 * dorabiał variant FX dla reveal phase, zaczął kopiować ten sam kod co Pet.ts
 * w runtime. DRY violation = divergencja gdy ktoś tweakuje tint w jednym
 * miejscu, w drugim zostaje stary. Centralne miejsce:
 *
 *  - **Stałe wizualne** (tint colors, czasy shimmer/rainbow, aura)
 *  - **Funkcje aplikujące** wariant na dowolny sprite — Pet (runtime FSM) ORAZ
 *    HatchDirector (reveal pet sprite w cinematic) wywołują tę samą funkcję.
 *
 * **Owner model:**
 * Funkcje tweens mutują property na "owner" obiekcie. Pet ma to jako class
 * fields (`rainbowHue`, `goldShimmerProgress`). HatchDirector tworzy local
 * obiekt VariantFxOwner per cinematic. Tween musi mieć target z mutable
 * property — można dać sprite jako target, ale property name'y by się gryzły
 * z wewnętrznymi Phaser tweens.
 *
 * **Cleanup:**
 * `applyVariantFx()` zwraca `VariantFxRefs` z aurą + tweens — caller
 * (Pet.destroy() lub HatchDirector.destroy()) przyjmuje odpowiedzialność
 * za cleanup. Helper nie trzyma globalnego state'u.
 */

// ── Stałe wizualne (extracted from Pet.ts sesja 29B) ────────────────────────

/** Promień subtelnej poświaty pod sprite'em (cień bardziej niż glow). */
export const AURA_RADIUS = 28;

/** Ciepłe złoto — sprite peta jako "złoty". */
export const GOLD_TINT_BASE = 0xffd24a;

/** Bardzo jasny kremowy — krótki specular flash na shimmer peak. */
export const GOLD_TINT_SHIMMER = 0xfffae0;

/** Co tyle ms odpala się gold shimmer cykl. */
export const GOLD_SHIMMER_PERIOD_MS = 2500;

/** Długość gold shimmer flash'a (in + out). */
export const GOLD_SHIMMER_FLASH_MS = 220;

/** Delikatny ciepły glow dla huge — NIE czerwony (huge = masa, nie kolor). */
export const HUGE_AURA_TINT = 0xffeecc;

/** Kosmiczny fiolet glow dla titanic — apex rozmiarowy ponad huge. */
export const TITANIC_AURA_TINT = 0xcbb0ff;

/** Pełen cykl hue 0→360 prismatic ringa dla rainbow variant. */
export const RAINBOW_CYCLE_MS = 1700;

/** Obrot prismatic ring wokol rainbow peta. Szybki, bo ring jest glownym FX. */
export const RAINBOW_AURA_ROTATE_MS = 2400;

/** Kolory segmentow prismatic ring / rainbow particle FX. */
export const RAINBOW_PRISM_TINTS = [0xff5fc8, 0xff5b5b, 0xffe45c, 0x8cff5f, 0x51f5ff, 0x7394ff, 0xb869ff, 0xff6dff];

/** Paleta iskierek rainbow. Bialy na poczatku daje specular feel. */
export const RAINBOW_SPARKLE_TINTS = [0xffffff, ...RAINBOW_PRISM_TINTS];

/** Rainbow ring ma wychodzic wyraznie poza pet sprite, zamiast chowac sie pod nim. */
export const RAINBOW_AURA_RADIUS_MULT = 1.62;

/** Huge variant pets są ~45% większe (skala na sprite scale, NIE tu liczone). */
export const HUGE_SCALE_MULT = 1.45;

/** Titanic variant pets są ~90% większe — wyraźnie ponad huge (Pet Simulator: Titanic > Huge). */
export const TITANIC_SCALE_MULT = 1.9;

// ── Iskry wariantu (sparkles) — implementacja flagi `VARIANT_VISUALS.sparkles` ─
// Tekstura: `fx-sparkle-star` (proceduralna, ładowana w PreloadScene), fallback
// do `fx-particle-white`. Gold/rainbow mają czytelny złoty ember niezależnie od ruchu.

/** Proceduralna czteroramienna iskra dla gold/rainbow petów. */
export const SPARKLE_TEXTURE_KEY = 'fx-sparkle-star';

/** Fallback dla starszych buildów / awaryjnego braku tekstury. */
export const SPARKLE_FALLBACK_TEXTURE_KEY = 'fx-particle-white';

/** Złote odcienie iskier (random per cząstka) — mocno widoczne dla gold/rainbow. */
export const GOLD_SPARKLE_TINTS = [0xffffff, 0xfff3c0, 0xffe27a, 0xffd24a, 0xffb52e];

/** Co ile ms ambient emitter wypuszcza iskry (stała, wyraźna poświata wokół peta). */
export const SPARKLE_EMIT_INTERVAL_MS = 70;

/** Ile iskier wypuszcza jeden ambient tick. */
export const SPARKLE_EMIT_QUANTITY = 3;

/** Co ile ms fallback sprite sparkle spawnują pety gold/rainbow. */
export const SPARKLE_SPRITE_INTERVAL_MS = 110;

/** Promień (px) strefy spawnu ambient iskier wokół środka peta. */
export const SPARKLE_EMIT_RADIUS = 28;

/** Ile iskier tryska w jednym wybuchu przy uderzeniu peta gold/rainbow. */
export const SPARKLE_BURST_COUNT = 12;

/** Rainbow ma rzadszy, ale bardziej efektowny prism burst przy trafieniu. */
export const RAINBOW_SPARKLE_BURST_COUNT = 18;

/** Życie cząstki w wybuchu przy uderzeniu (ms). */
export const SPARKLE_BURST_LIFE_MS = 520;

/** Minimalna predkosc peta (px/s), od ktorej rainbow zostawia afterimage trail. */
export const RAINBOW_TRAIL_SPEED_THRESHOLD = 55;

/** Minimalny odstep miedzy rainbow afterimage'ami w runtime world. */
export const RAINBOW_TRAIL_INTERVAL_MS = 58;

/** Czas wygaszania pojedynczego rainbow afterimage'a. */
export const RAINBOW_TRAIL_LIFE_MS = 420;

/**
 * Mnożnik skali sprite'a zależny od wariantu. Single source of truth dla
 * Pet.ts (runtime), StallPet.ts (paddock) ORAZ HatchDirector/BulkDirector
 * (cinematic) — żeby huge/titanic skalowały się spójnie wszędzie.
 * normal/gold/rainbow = 1 (wariant to nakładka, nie rozmiar).
 */
export function variantScaleMult(variant: Variant): number {
  if (variant === 'titanic') return TITANIC_SCALE_MULT;
  if (variant === 'huge') return HUGE_SCALE_MULT;
  return 1;
}

// ── Owner pattern ──────────────────────────────────────────────────────────

/**
 * Obiekt z mutable properties które Phaser tween modyfikuje przez `onUpdate`.
 * Caller (Pet, HatchDirector) musi mieć stabilny instance owner przez cały
 * czas życia tween'a (inaczej Phaser GC nie zna target'u).
 *
 * `Pet` ma to jako class fields w sobie (sam jest ownerem).
 * `HatchDirector` tworzy `{ goldShimmerProgress: 0, rainbowHue: 0 }` jako
 * local owner per cinematic (ginie z `destroy()`).
 */
export interface VariantFxOwner {
  goldShimmerProgress: number;
  rainbowHue: number;
}

// ── Refs do cleanup ────────────────────────────────────────────────────────

export interface VariantFxRefs {
  /** Aura graphics (gold/rainbow/huge/titanic) — null dla normal. */
  aura: Phaser.GameObjects.Graphics | null;
  /** Wszystkie tweens do `tween.remove()` w cleanup. */
  tweens: Phaser.Tweens.Tween[];
  /** Ambient sparkle emittery (opt-in `sparkleEmitter`) do `emitter.destroy()` w cleanup. */
  emitters: Phaser.GameObjects.Particles.ParticleEmitter[];
}

// ── Main entry: applyVariantFx ─────────────────────────────────────────────

/**
 * Aplikuje wizualny efekt wariantu na dany sprite peta.
 *
 *  - `normal`  → no-op (zwraca puste refs)
 *  - `gold`    → tint base + shimmer flash co 2.5s + subtle aura
 *  - `rainbow` → bazowy sprite bez tintu + mocny prismatic aura ring
 *  - `huge`    → subtle warm aura (scale ustawia caller — patrz HUGE_SCALE_MULT)
 *  - `titanic` → mocniejsza fioletowa aura (scale — patrz TITANIC_SCALE_MULT)
 *
 * @param scene       Scene Phaser
 * @param sprite      Pet sprite (Image lub Sprite z `setTint`)
 * @param variant     'normal' | 'gold' | 'rainbow' | 'huge'
 * @param owner       Obiekt z mutable `goldShimmerProgress` i `rainbowHue` —
 *                    Phaser tween modyfikuje przez onUpdate.
 * @param auraPos     Pozycja aury (gdy gold/huge). Pet: logicalX/Y; HatchDirector:
 *                    pet sprite position (zazwyczaj 0,0 jeśli pet w containerze).
 * @param auraRadius  Opcjonalny override promienia aury (default = `AURA_RADIUS = 28px`).
 *                    Sesja 29B review fix MEDIUM B: w hatch cinematic pet sprite
 *                    jest skalowany do ~45% viewportu (np. 162px na mobile),
 *                    a default 28px byłby schowany pod sprite'em. HatchDirector
 *                    przekazuje radius liczony z petDisplayPx.
 * @param opts        `sparkleEmitter` (default false) — czy doczepić ciągły
 *                    ambient emitter złotych iskier dla wariantów gold/rainbow.
 *                    Włącza TYLKO świat (Pet.ts, max 6 petów). Plac (StallPet,
 *                    do 192 sprite'ów) i hatch NIE włączają — perf.
 * @returns           Refs do cleanup — aura + tweens + emittery.
 */
export function applyVariantFx(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  variant: Variant,
  owner: VariantFxOwner,
  auraPos: { x: number; y: number },
  auraRadius: number = AURA_RADIUS,
  opts: { sparkleEmitter?: boolean } = {},
): VariantFxRefs {
  const refs: VariantFxRefs = { aura: null, tweens: [], emitters: [] };

  if (variant === 'normal') return refs;

  if (variant === 'gold') {
    sprite.setTint(GOLD_TINT_BASE);
    refs.aura = installSubtleAura(scene, GOLD_TINT_BASE, 0.18, auraPos, auraRadius);
    refs.tweens.push(installGoldShimmer(scene, sprite, owner));
  } else if (variant === 'rainbow') {
    sprite.clearTint();                          // pet art ma zostac czytelny
    owner.rainbowHue = Phaser.Math.Between(0, 359);
    const rainbowAuraRadius = auraRadius * RAINBOW_AURA_RADIUS_MULT;
    refs.aura = installPrismaticAura(scene, auraPos, rainbowAuraRadius, owner.rainbowHue);
    refs.tweens.push(...installPrismaticAuraMotion(scene, refs.aura, owner, rainbowAuraRadius));
  } else if (variant === 'huge') {
    refs.aura = installSubtleAura(scene, HUGE_AURA_TINT, 0.15, auraPos, auraRadius);
  } else if (variant === 'titanic') {
    // Mocniejsza, nieco większa aura niż huge — apex rozmiarowy.
    refs.aura = installSubtleAura(scene, TITANIC_AURA_TINT, 0.22, auraPos, auraRadius * 1.1);
  }

  // Ambient iskry (opt-in) — tylko warianty gold/rainbow mają złoty emitter.
  if (opts.sparkleEmitter && VARIANT_VISUALS[variant].sparkles) {
    const emitter = installSparkleEmitter(scene, sprite, variant);
    if (emitter) refs.emitters.push(emitter);
  }

  return refs;
}

// ── Individual installers (eksportowane dla caller'ów którzy chcą szczegóły) ─

/**
 * Słaba aura jako "cień plus" pod sprite'em — alpha 0.15-0.20, bez pulsowania.
 * Sesja 23d: zastąpiła pulsujący "magic circle" z sesji 23 (Codex feedback —
 * tamto wyglądało jak buff bubble, nie jak wariant peta).
 */
export function installSubtleAura(
  scene: Phaser.Scene,
  color: number,
  alpha: number,
  pos: { x: number; y: number },
  radius = AURA_RADIUS,
): Phaser.GameObjects.Graphics {
  const aura = scene.add.graphics();
  aura.setDepth(4);                          // pod sprite (5)
  aura.setBlendMode(Phaser.BlendModes.ADD);   // additive — świeci
  aura.setPosition(pos.x, pos.y);
  aura.fillStyle(color, 1);
  aura.fillCircle(0, 0, radius);
  aura.setAlpha(alpha);
  // Brak tween na scale/alpha — wariant ma być stabilny, nie dynamiczny buff.
  return aura;
}

/**
 * Rainbow aura: miekkie biale halo + obracany segmentowy ring. To jest
 * drozsze wizualnie niz gold aura, ale nadal jeden Graphics + dwa tweens.
 */
export function installPrismaticAura(
  scene: Phaser.Scene,
  pos: { x: number; y: number },
  radius = AURA_RADIUS,
  baseHue = 0,
): Phaser.GameObjects.Graphics {
  const aura = scene.add.graphics();
  aura.setDepth(4);
  aura.setBlendMode(Phaser.BlendModes.ADD);
  aura.setPosition(pos.x, pos.y);
  aura.setRotation(Phaser.Math.FloatBetween(0, Math.PI * 2));
  aura.setAlpha(0.98);
  drawPrismaticAura(aura, radius, baseHue);

  return aura;
}

export function installPrismaticAuraMotion(
  scene: Phaser.Scene,
  aura: Phaser.GameObjects.Graphics,
  owner: VariantFxOwner,
  radius: number,
): Phaser.Tweens.Tween[] {
  const spin = scene.tweens.add({
    targets: aura,
    rotation: aura.rotation + Math.PI * 2,
    duration: RAINBOW_AURA_ROTATE_MS,
    repeat: -1,
    ease: 'Linear',
  });
  const colorCycle = scene.tweens.add({
    targets: owner,
    rainbowHue: owner.rainbowHue + 360,
    duration: RAINBOW_CYCLE_MS,
    repeat: -1,
    ease: 'Linear',
    onUpdate: () => drawPrismaticAura(aura, radius, owner.rainbowHue),
    onRepeat: () => drawPrismaticAura(aura, radius, owner.rainbowHue),
  });
  const pulse = scene.tweens.add({
    targets: aura,
    scaleX: 1.13,
    scaleY: 1.13,
    alpha: 0.78,
    duration: 720,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
  return [spin, colorCycle, pulse];
}

function drawPrismaticAura(g: Phaser.GameObjects.Graphics, radius: number, baseHue: number): void {
  g.clear();

  const coreRadius = radius * 0.68;
  g.fillStyle(0xffffff, 0.13);
  g.fillCircle(0, 0, coreRadius);
  g.fillStyle(hsvToHex(baseHue + 175, 0.62, 1), 0.075);
  g.fillCircle(0, 0, coreRadius * 0.82);

  strokePrismaticRing(g, radius * 1.08, Math.max(5, radius * 0.18), 0.96, baseHue);
  strokePrismaticRing(g, radius * 0.78, Math.max(2.5, radius * 0.07), 0.58, baseHue + 180);
  g.lineStyle(Math.max(1.5, radius * 0.045), 0xffffff, 0.7);
  g.strokeCircle(0, 0, radius * 0.91);
}

function strokePrismaticRing(
  g: Phaser.GameObjects.Graphics,
  radius: number,
  width: number,
  alpha: number,
  hueOffset = 0,
): void {
  const segment = (Math.PI * 2) / RAINBOW_PRISM_TINTS.length;
  const gap = Phaser.Math.DegToRad(3);
  for (let i = 0; i < RAINBOW_PRISM_TINTS.length; i++) {
    const hue = hueOffset + (i / RAINBOW_PRISM_TINTS.length) * 360;
    g.lineStyle(width, hsvToHex(hue, 0.92, 1), alpha);
    g.beginPath();
    g.arc(0, 0, radius, i * segment + gap, (i + 1) * segment - gap, false);
    g.strokePath();
  }
}

/**
 * Złoty shimmer: co GOLD_SHIMMER_PERIOD_MS interpoluje tint sprite'a z
 * GOLD_TINT_BASE → GOLD_TINT_SHIMMER → GOLD_TINT_BASE w ciągu GOLD_SHIMMER_FLASH_MS.
 * Imituje błysk światła odbity od metalu.
 *
 * Implementacja: tween na property `owner.goldShimmerProgress` (0→1→0 yoyo)
 * z ColorWithColor interpolation w onUpdate. Owner musi mieć mutable property.
 */
export function installGoldShimmer(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  owner: VariantFxOwner,
): Phaser.Tweens.Tween {
  const baseColor = Phaser.Display.Color.IntegerToColor(GOLD_TINT_BASE);
  const flashColor = Phaser.Display.Color.IntegerToColor(GOLD_TINT_SHIMMER);

  return scene.tweens.add({
    targets: owner,
    goldShimmerProgress: 1,
    duration: GOLD_SHIMMER_FLASH_MS / 2,
    yoyo: true,
    delay: GOLD_SHIMMER_PERIOD_MS - GOLD_SHIMMER_FLASH_MS,
    repeatDelay: GOLD_SHIMMER_PERIOD_MS - GOLD_SHIMMER_FLASH_MS,
    repeat: -1,
    ease: 'Sine.easeInOut',
    onUpdate: () => {
      const c = Phaser.Display.Color.Interpolate.ColorWithColor(
        baseColor,
        flashColor,
        100,
        Math.round(owner.goldShimmerProgress * 100),
      );
      const hex = Phaser.Display.Color.GetColor(c.r, c.g, c.b);
      sprite.setTint(hex);
    },
  });
}

/**
 * Aplikuje 4-corner gradient tint na sprite z rotującym hue.
 * 4 corners (TL, TR, BL, BR) dostają hue offset 0°, 90°, 180°, 270° od baseHue.
 * Efekt: tęczowa "fala" przesuwa się po sprite peta co RAINBOW_CYCLE_MS.
 */
export function applyRainbowTint(
  sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  baseHue: number,
): void {
  const h = baseHue;
  const tl = hsvToHex(h,         0.7, 1.0);
  const tr = hsvToHex(h +  90,   0.7, 1.0);
  const bl = hsvToHex(h + 270,   0.7, 1.0);
  const br = hsvToHex(h + 180,   0.7, 1.0);
  sprite.setTint(tl, tr, bl, br);
}

/**
 * Rainbow tween — animuje `owner.rainbowHue` 0→360 w pętli, na każdy update
 * woła `applyRainbowTint`. Zwraca tween żeby caller go cleanu'pował.
 */
export function installRainbowTint(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  owner: VariantFxOwner,
): Phaser.Tweens.Tween {
  return scene.tweens.add({
    targets: owner,
    rainbowHue: 360,
    duration: RAINBOW_CYCLE_MS,
    repeat: -1,
    onUpdate: () => applyRainbowTint(sprite, owner.rainbowHue),
  });
}

// ── Iskry (sparkles) ────────────────────────────────────────────────────────

/** Gold ma metaliczne iskry; rainbow uzywa osobnej prismatic palety. */
function sparkleTints(variant: Variant): number[] {
  return variant === 'rainbow' ? RAINBOW_SPARKLE_TINTS : GOLD_SPARKLE_TINTS;
}

function sparkleTextureKey(scene: Phaser.Scene): string | null {
  if (scene.textures.exists(SPARKLE_TEXTURE_KEY)) return SPARKLE_TEXTURE_KEY;
  if (scene.textures.exists(SPARKLE_FALLBACK_TEXTURE_KEY)) return SPARKLE_FALLBACK_TEXTURE_KEY;
  return null;
}

export function variantHasGoldSparkles(variant: Variant): boolean {
  return Boolean(VARIANT_VISUALS[variant].sparkles);
}

export function spawnAmbientSparkleSprite(scene: Phaser.Scene, x: number, y: number, variant: Variant = 'gold'): void {
  const textureKey = sparkleTextureKey(scene);
  if (!textureKey) return;

  const tints = sparkleTints(variant);
  const tint = tints[Phaser.Math.Between(0, tints.length - 1)]!;
  const spreadX = variant === 'rainbow' ? 28 : 22;
  const spreadY = variant === 'rainbow' ? 24 : 20;
  const startX = x + Phaser.Math.Between(-spreadX, spreadX);
  const startY = y + Phaser.Math.Between(-spreadY, 14);
  const sparkle = scene.add.image(startX, startY, textureKey);
  sparkle.setDepth(31);
  sparkle.setTint(tint);
  sparkle.setAlpha(1);
  sparkle.setBlendMode(variant === 'rainbow' ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL);
  sparkle.setScale(Phaser.Math.FloatBetween(0.68, variant === 'rainbow' ? 1.18 : 1.05));
  sparkle.setAngle(Phaser.Math.Between(0, 359));

  scene.tweens.add({
    targets: sparkle,
    x: startX + Phaser.Math.Between(-18, 18),
    y: startY - Phaser.Math.Between(34, 62),
    alpha: 0,
    scale: 0,
    angle: sparkle.angle + Phaser.Math.Between(-220, 220),
    duration: Phaser.Math.Between(430, 720),
    ease: 'Sine.easeOut',
    onComplete: () => sparkle.destroy(),
  });
}

/**
 * Ciągły ambient emitter iskier doczepiony do sprite'a peta (śledzi go przez
 * `setPosition` w `Pet.update`). Iskry stale tryskają ku górze jak złoty ember —
 * pet gold/rainbow jest czytelny także gdy tylko biegnie albo stoi w formacji.
 *
 * **Tylko świat (max 6 equipped petów).** Caller (`applyVariantFx` z
 * `opts.sparkleEmitter=true`) odpowiada za włączenie; plac i hatch NIE używają
 * (perf — patrz docstring applyVariantFx). Zwraca emitter do cleanup; null gdy
 * brak tekstury.
 */
export function installSparkleEmitter(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  variant: Variant,
): Phaser.GameObjects.Particles.ParticleEmitter | null {
  const textureKey = sparkleTextureKey(scene);
  if (!textureKey) {
    console.warn('[variantFx] Brak tekstury iskier — sparkle emitter pominięty');
    return null;
  }
  const emitter = scene.add.particles(sprite.x, sprite.y, textureKey, {
    lifespan: { min: 460, max: 820 },
    speed: { min: 48, max: 145 },
    angle: { min: 215, max: 325 },                 // tryska ku górze z szerokim rozrzutem
    scale: { start: 0.95, end: 0.12 },
    alpha: { start: 1, end: 0.08 },
    rotate: { min: 0, max: 360 },
    gravityY: -70,
    frequency: SPARKLE_EMIT_INTERVAL_MS,
    quantity: SPARKLE_EMIT_QUANTITY,
    tint: sparkleTints(variant),
    blendMode: variant === 'rainbow' ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL,
    // Cast: Geom.Circle ma generyczne `getRandomPoint<O>()`, a typ Phasera
    // oczekuje `(point) => void` — runtime OK (Phaser tak używa Geom jako zone).
    emitZone: {
      type: 'random',
      source: new Phaser.Geom.Circle(0, 0, SPARKLE_EMIT_RADIUS),
    } as Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig,
  });
  emitter.setDepth(30);                             // nad petem i większością world FX
  emitter.explode(SPARKLE_EMIT_QUANTITY * 3, sprite.x, sprite.y);
  return emitter;
}

/**
 * Jednorazowy wybuch iskier w punkcie (x, y) — "tryskające sparks" przy
 * uderzeniu peta gold/rainbow w obiekt. No-op dla wariantów bez flagi `sparkles`
 * (czyli `normal`). Emitter sam się niszczy po wygaśnięciu cząstek.
 */
export function spawnSparkleBurst(
  scene: Phaser.Scene,
  x: number,
  y: number,
  variant: Variant,
): void {
  if (!VARIANT_VISUALS[variant].sparkles) return;
  const textureKey = sparkleTextureKey(scene);
  if (!textureKey) return;

  const isRainbow = variant === 'rainbow';
  const burstLife = isRainbow ? SPARKLE_BURST_LIFE_MS + 120 : SPARKLE_BURST_LIFE_MS;
  const emitter = scene.add.particles(x, y, textureKey, {
    lifespan: burstLife,
    speed: { min: 70, max: 190 },
    angle: { min: 0, max: 360 },
    scale: { start: 0.86, end: 0 },
    alpha: { start: 1, end: 0 },
    rotate: { min: 0, max: 360 },
    gravityY: 120,
    tint: sparkleTints(variant),
    blendMode: Phaser.BlendModes.ADD,
    emitting: false,
  });
  emitter.setDepth(21);                             // nad coin burst flash (18-20)
  emitter.explode(isRainbow ? RAINBOW_SPARKLE_BURST_COUNT : SPARKLE_BURST_COUNT, x, y);
  if (isRainbow) spawnRainbowImpactRing(scene, x, y);
  scene.time.delayedCall(burstLife + 150, () => emitter.destroy());
}

function spawnRainbowImpactRing(scene: Phaser.Scene, x: number, y: number): void {
  const ring = scene.add.graphics();
  ring.setDepth(20);
  ring.setPosition(x, y);
  ring.setBlendMode(Phaser.BlendModes.ADD);
  strokePrismaticRing(ring, 15, 3, 0.9);
  ring.lineStyle(1.5, 0xffffff, 0.85);
  ring.strokeCircle(0, 0, 10);
  ring.setScale(0.45);
  ring.setAlpha(0.95);

  scene.tweens.add({
    targets: ring,
    scaleX: 1.9,
    scaleY: 1.9,
    alpha: 0,
    duration: 300,
    ease: 'Quad.easeOut',
    onComplete: () => ring.destroy(),
  });
}

/**
 * Krotki ghost sprite zostawiany tylko przez worldowe rainbow pety w ruchu.
 * Hatch/plaza/stall nie wlaczaja tego automatycznie, bo tam liczy sie LOD.
 */
export function spawnRainbowTrailAfterimage(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  baseHue: number,
  velocityX = 0,
  velocityY = 0,
): void {
  const speed = Math.hypot(velocityX, velocityY);
  const backX = speed > 0 ? -velocityX / speed : 0;
  const backY = speed > 0 ? -velocityY / speed : 0;
  const layers = [
    { offset: 0, alpha: 0.6, scale: 1.1, hue: 35, life: RAINBOW_TRAIL_LIFE_MS },
    { offset: 11, alpha: 0.44, scale: 1.18, hue: 155, life: RAINBOW_TRAIL_LIFE_MS - 45 },
    { offset: 22, alpha: 0.28, scale: 1.27, hue: 275, life: RAINBOW_TRAIL_LIFE_MS - 90 },
  ];

  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i]!;
    const startX = sprite.x + backX * layer.offset;
    const startY = sprite.y + backY * layer.offset;
    const trail = scene.add.image(startX, startY, sprite.texture.key, sprite.frame.name);
    trail.setOrigin(sprite.originX, sprite.originY);
    trail.setScale(sprite.scaleX * layer.scale, sprite.scaleY * layer.scale);
    trail.setRotation(sprite.rotation);
    trail.setFlipX(sprite.flipX);
    trail.setFlipY(sprite.flipY);
    trail.setDepth(sprite.depth - 0.35 - i * 0.02);
    trail.setBlendMode(Phaser.BlendModes.ADD);
    trail.setAlpha(layer.alpha);
    applyRainbowTint(trail, baseHue + layer.hue);

    scene.tweens.add({
      targets: trail,
      x: startX + backX * (18 + i * 7),
      y: startY + backY * (18 + i * 7),
      scaleX: sprite.scaleX * (layer.scale + 0.16),
      scaleY: sprite.scaleY * (layer.scale + 0.16),
      alpha: 0,
      duration: layer.life,
      ease: 'Sine.easeOut',
      onComplete: () => trail.destroy(),
    });
  }
}

// ── HSV → Hex ──────────────────────────────────────────────────────────────

/**
 * Konwertuje HSV (h: 0-360, s: 0-1, v: 0-1) → 0xRRGGBB (24-bit int).
 *
 * Algorytm standardowy — patrz https://en.wikipedia.org/wiki/HSL_and_HSV
 * sekcja "HSV to RGB". Implementacja inline (zero dependency).
 *
 * Używane przez rainbow variant + przyszłe FX które potrzebują tęczowych
 * kolorów (np. rainbow rarity preset w hatchPresets.config.ts).
 */
export function hsvToHex(h: number, s: number, v: number): number {
  const hNorm = ((h % 360) + 360) % 360;
  const c = v * s;
  const x = c * (1 - Math.abs(((hNorm / 60) % 2) - 1));
  const m = v - c;

  let r = 0, g = 0, b = 0;
  if (hNorm < 60)       { r = c; g = x; b = 0; }
  else if (hNorm < 120) { r = x; g = c; b = 0; }
  else if (hNorm < 180) { r = 0; g = c; b = x; }
  else if (hNorm < 240) { r = 0; g = x; b = c; }
  else if (hNorm < 300) { r = x; g = 0; b = c; }
  else                  { r = c; g = 0; b = x; }

  const R = Math.round((r + m) * 255);
  const G = Math.round((g + m) * 255);
  const B = Math.round((b + m) * 255);
  return (R << 16) | (G << 8) | B;
}

/**
 * Cleanup helper — niszczy tweens i aurę z `VariantFxRefs`.
 * Idempotent (multiple calls OK).
 */
export function destroyVariantFx(refs: VariantFxRefs): void {
  for (const tween of refs.tweens) {
    tween.remove();
  }
  refs.tweens.length = 0;
  // Emittery śledzą sprite (startFollow) — niszcz PRZED sprite.destroy() (caller
  // robi to w tej kolejności), inaczej follow target byłby zombie.
  for (const emitter of refs.emitters) {
    emitter.destroy();
  }
  refs.emitters.length = 0;
  if (refs.aura) {
    refs.aura.destroy();
    refs.aura = null;
  }
}
